import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignItemActions from '@redux/modules/campaignItem'

import defaultFormState, { requiredFields } from '@models/campaignItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, EntityOptions, ModuleState } from '@redux/modules/types'
import type { CampaignItemModel, CampaignItemRequestOptions } from '@models/types'

export const generateUrls = (campaignItem?: Partial<CampaignItemModel>) => {
  const { id } = campaignItem || {}

  return {
    editCampaignItemDetailsUrl: `#/systemManager/campaignItems/${id}/edit/details`,
    editCampaignItemUrl: `#/systemManager/campaignItems/${id}/edit/`,
    campaignItemsIndexUrl: '#/systemManager/campaignItems',
  }
}

type CreateCampaignItemParams = {
  campaignItemParams: Partial<CampaignItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignItemRequestOptions,
}

const createCampaignItem = (params: CreateCampaignItemParams) => {
  const { dispatch, campaignItemParams, requestOptions } = params
  const { createCampaignItem: createFn } = campaignItemActions

  return dispatch(createFn(campaignItemParams, requestOptions))
}

type UpdateCampaignItemParams = {
  campaignItem: CampaignItemModel,
  campaignItemParams: Partial<CampaignItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignItemRequestOptions,
}

const updateCampaignItem = (params: UpdateCampaignItemParams) => {
  const {
    dispatch, campaignItem, campaignItemParams, requestOptions,
  } = params
  const { updateCampaignItem: updateFn } = campaignItemActions

  const updatedParams = {
    id: campaignItem.id,
    ...campaignItemParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type BulkReconcileParams = {
  entity: Partial<CampaignItemModel>,
  entityIds: number[],
}

type BulkReconcileCampaignItemParams = {
  bulkReconcileParams: BulkReconcileParams,
  campaign: Partial<CampaignItemModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const bulkReconcileCampaignItems = (params: BulkReconcileCampaignItemParams) => {
  const {
    bulkReconcileParams, dispatch, requestOptions,
  } = params

  const { entityIds, entity } = bulkReconcileParams

  const { bulkReconcileCampaignItems: bulkReconcileFn } = campaignItemActions

  const updatedParams = {
    campaignItem: entity,
    campaignItemIds: entityIds,
  }

  return dispatch(bulkReconcileFn(updatedParams, requestOptions))
}

type DeleteCampaignItemParams = {
  campaignItem: DeleteParams<CampaignItemModel>,
  dispatch: AppDispatch,
}

const deleteCampaignItem = (params: DeleteCampaignItemParams) => {
  const { dispatch, campaignItem } = params
  const { deleteCampaignItem: deleteFn } = campaignItemActions

  return dispatch(deleteFn(campaignItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignItemForm(
  campaignItem: Partial<CampaignItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignItemForm = useForm(
    defaultFormState,
    { entity: campaignItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignItem.id, campaignItem.cache_key],
  )

  return {
    ...campaignItemForm,
  }
}

export const useRelations = (campaignItem: Partial<CampaignItemModel> = {}) => {
  const {
    campaign_id,
    campaign_quote_id,
    custom_item_id,
    user_id,
  } = campaignItem

  const {
    campaigns,
    campaignQuotes,
    customItems,
    users,
  } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  const campaignQuote = campaign_quote_id && campaignQuotes[campaign_quote_id] ? campaignQuotes[campaign_quote_id] : {}
  const customItem = custom_item_id && customItems[custom_item_id] ? customItems[custom_item_id] : {}
  const user = user_id && users[user_id] ? users[user_id] : {}

  return {
    campaign,
    campaignQuote,
    customItem,
    user,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignItemRequestOptions,
}

function useCampaignItem(initEntity: Partial<CampaignItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: campaignItem }: { entity: CampaignItemModel} = useLatestEntity(initEntity, 'campaignItems')
  const { id } = campaignItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    campaign, campaignQuote, customItem, user,
  } = useRelations(campaignItem)

  useReduxAction(
    'campaignItems',
    'loadCampaignItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignItems)

  return {
    callbacks: {
      createCampaignItem: (
        campaignItemParams: Partial<CampaignItemModel>,
        entityOptions?: CampaignItemRequestOptions,
      ) => (
        createCampaignItem({
          campaignItemParams,
          dispatch,
          requestOptions: entityOptions,
        })
      ),
      createCreditNoteAndDeleteOrder: (deleteOrderParams: { deleteFn: () => void }) => launchModal({
        callbacks,
        modalKey: 'CreateCreditNoteAndDeleteOrderModal',
        payload: {
          entity: campaignItem,
          ...deleteOrderParams,
        },
      }),
      createOrEditCampaignItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignItemModal',
        payload: { campaignItem },
      }),
      bulkReconcileCampaignItems: (
        bulkReconcileParams: BulkReconcileParams,
        entityOptions?: EntityOptions,
      // @ts-expect-error TS2345
      ) => bulkReconcileCampaignItems({ bulkReconcileParams, dispatch, requestOptions: entityOptions }),
      deleteCampaignItem: () => deleteCampaignItem({ dispatch, campaignItem }),
      updateCampaignItem: (
        campaignItemParams: Partial<CampaignItemModel>,
        entityOptions?: CampaignItemRequestOptions,
      ) => (
        updateCampaignItem({
          campaignItem,
          campaignItemParams,
          dispatch,
          requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    campaignItem,
    campaignQuote,
    creating,
    customItem,
    deleting,
    loading: loading && !loadedIds.includes(campaignItem.id),
    updating,
    urls: generateUrls(campaignItem),
    user,
  }
}

export type UseCampaignItemPayload = ReturnType<typeof useCampaignItem>

export default useCampaignItem
